import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// MARK://dhk8/ui-restaurant/LockScreen
// Full-bleed keypad 6-digit PIN, sessionStorage `dhk8.lock` state, fires dhk8:rehydrate on unlock.
// Port de sc-restaurant LockScreen.tsx.
import { useEffect, useState } from 'react';
import { Delete } from 'lucide-react';
import { Button } from '@dockethub/ui';
const PIN_LENGTH = 6;
const STORAGE_KEY = 'dhk8.lock';
/** Mark the session as locked. */
export function lockSession() {
    try {
        sessionStorage.setItem(STORAGE_KEY, 'locked');
    }
    catch { /* ignore */ }
}
/** Check if session is locked. */
export function isSessionLocked() {
    try {
        return sessionStorage.getItem(STORAGE_KEY) === 'locked';
    }
    catch {
        return false;
    }
}
const KEYS = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['', '0', 'del'],
];
export function LockScreen({ appName = 'DHK8', branchLabel, onUnlock, userName, }) {
    const [digits, setDigits] = useState([]);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    // Keyboard support
    useEffect(() => {
        function handle(e) {
            if (busy)
                return;
            if (e.key >= '0' && e.key <= '9') {
                addDigit(e.key);
            }
            else if (e.key === 'Backspace') {
                removeDigit();
            }
        }
        window.addEventListener('keydown', handle);
        return () => window.removeEventListener('keydown', handle);
    });
    function addDigit(d) {
        setDigits((prev) => {
            if (prev.length >= PIN_LENGTH)
                return prev;
            const next = [...prev, d];
            if (next.length === PIN_LENGTH) {
                void submit(next.join(''));
            }
            return next;
        });
        setError(null);
    }
    function removeDigit() {
        setDigits((prev) => prev.slice(0, -1));
        setError(null);
    }
    async function submit(pin) {
        setBusy(true);
        setError(null);
        try {
            await onUnlock(pin);
            // On success: clear lock state and fire rehydrate event
            try {
                sessionStorage.removeItem(STORAGE_KEY);
            }
            catch { /* ignore */ }
            window.dispatchEvent(new CustomEvent('dhk8:rehydrate'));
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'PIN incorrecto');
            setDigits([]);
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsxs("div", { className: "dhk8-lock", role: "main", "aria-label": "Pantalla bloqueada", children: [_jsxs("div", { className: "dhk8-lock__inner", children: [_jsxs("div", { className: "dhk8-lock__brand", children: [_jsx("div", { className: "dhk8-lock__app", children: appName }), branchLabel !== undefined && (_jsx("div", { className: "dhk8-lock__branch", children: branchLabel }))] }), userName !== undefined && (_jsxs("div", { className: "dhk8-lock__user", children: [_jsx("span", { className: "dhk8-lock__user-label", children: "Ingresa el PIN de" }), _jsx("span", { className: "dhk8-lock__user-name", children: userName })] })), _jsx("div", { className: "dhk8-lock__dots", "aria-hidden": "true", children: Array.from({ length: PIN_LENGTH }).map((_, i) => (_jsx("span", { className: `dhk8-lock__dot${i < digits.length ? ' dhk8-lock__dot--filled' : ''}` }, i))) }), error !== null && (_jsx("div", { className: "dhk8-lock__error", role: "alert", children: error })), _jsx("div", { className: "dhk8-lock__keypad", "aria-label": "Teclado num\u00E9rico", children: KEYS.flat().map((key) => {
                            if (key === '') {
                                return _jsx("div", { className: "dhk8-lock__key dhk8-lock__key--empty" }, "empty");
                            }
                            if (key === 'del') {
                                return (_jsx("button", { type: "button", className: "dhk8-lock__key dhk8-lock__key--del", "aria-label": "Borrar", disabled: busy || digits.length === 0, onClick: removeDigit, children: _jsx(Delete, { size: 20 }) }, "del"));
                            }
                            return (_jsx("button", { type: "button", className: "dhk8-lock__key", "aria-label": key, disabled: busy || digits.length >= PIN_LENGTH, onClick: () => addDigit(key), children: key }, key));
                        }) }), digits.length === PIN_LENGTH && !busy && (_jsx(Button, { variant: "primary", size: "lg", block: true, onClick: () => void submit(digits.join('')), children: "Desbloquear" }))] }), _jsx("style", { children: lockStyles })] }));
}
const lockStyles = `
  /* HIG-green lock screen: rounded keypad, soft hairlines, full-fill press state.
     Reads the host app's --pos-* tokens; falls back to legacy @dockethub/ui aliases,
     then to literal Restaurant-Dark values so it never goes brutalist. */
  .dhk8-lock {
    position: fixed;
    inset: 0;
    z-index: var(--z-modal, 1000);
    background: var(--pos-canvas, var(--bg-canvas, var(--bg, #0F1411)));
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--pos-space-8, var(--space-8, 32px));
  }
  .dhk8-lock__inner {
    width: 100%;
    max-width: 320px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--pos-space-6, var(--space-6, 24px));
    padding: var(--pos-space-8, 32px);
    background: var(--pos-surface, var(--bg-surface, var(--surface, #161C18)));
    border: 1px solid var(--pos-separator, var(--separator-opaque, rgba(255,255,255,.07)));
    border-radius: var(--pos-radius-2xl, 24px);
    box-shadow: var(--pos-shadow-3, 0 8px 20px -6px rgba(0,0,0,.35));
  }
  .dhk8-lock__brand { text-align: center; }
  .dhk8-lock__app { font-family: var(--font-display, inherit); font-size: var(--pos-text-2xl, var(--text-2xl, 1.5rem)); font-weight: var(--pos-weight-semibold, 600); color: var(--pos-fg, var(--text-primary, var(--fg, #E8EFE9))); letter-spacing: var(--pos-tracking-snug, -0.005em); }
  .dhk8-lock__branch { font-family: var(--font-display, inherit); font-size: var(--pos-text-xs, var(--text-xs, .75rem)); color: var(--pos-fg-tertiary, var(--text-tertiary, var(--fg-muted, #7E8C82))); margin-top: var(--pos-space-1, 4px); }
  .dhk8-lock__user {
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: var(--pos-space-1, 4px);
  }
  .dhk8-lock__user-label { font-family: var(--font-display, inherit); font-size: var(--pos-text-xs, var(--text-xs, .75rem)); color: var(--pos-fg-tertiary, var(--text-tertiary, var(--fg-muted, #7E8C82))); }
  .dhk8-lock__user-name { font-family: var(--font-display, inherit); font-size: var(--pos-text-md, var(--text-md, .9375rem)); font-weight: var(--pos-weight-medium, 500); color: var(--pos-fg, var(--text-primary, var(--fg, #E8EFE9))); }
  .dhk8-lock__dots {
    display: flex;
    gap: var(--pos-space-3, var(--space-3, 12px));
    align-items: center;
    justify-content: center;
  }
  .dhk8-lock__dot {
    width: 12px;
    height: 12px;
    border: 1.5px solid var(--pos-separator-strong, var(--separator, rgba(255,255,255,.14)));
    background: transparent;
    border-radius: 50%;
    transition: background var(--pos-duration-fast, 120ms) var(--pos-ease-out, ease), border-color var(--pos-duration-fast, 120ms) var(--pos-ease-out, ease), transform var(--pos-duration-fast, 120ms) var(--pos-ease-out, ease);
  }
  .dhk8-lock__dot--filled { background: var(--pos-tint, var(--accent, #1F8B3A)); border-color: var(--pos-tint, var(--accent, #1F8B3A)); transform: scale(1.08); }
  .dhk8-lock__error {
    font-family: var(--font-display, inherit);
    font-size: var(--pos-text-xs, var(--text-xs, .75rem));
    color: var(--pos-danger, var(--destructive, #E5484D));
    text-align: center;
    padding: var(--pos-space-2, 8px) var(--pos-space-3, 12px);
    background: color-mix(in oklch, var(--pos-danger, #E5484D) 14%, transparent);
    border-radius: var(--pos-radius-md, 8px);
    width: 100%;
  }
  .dhk8-lock__keypad {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: var(--pos-space-2, var(--space-2, 8px));
    width: 100%;
  }
  .dhk8-lock__key {
    font-family: var(--font-display, inherit);
    font-size: var(--pos-text-xl, var(--text-xl, 1.25rem));
    font-weight: var(--pos-weight-medium, 500);
    font-variant-numeric: tabular-nums;
    min-height: var(--pos-touch-target-lg, 56px);
    height: 64px;
    background: var(--pos-elevated, var(--surface-elevated, var(--surface, #1E2520)));
    border: 1px solid var(--pos-separator, var(--separator-opaque, rgba(255,255,255,.07)));
    color: var(--pos-fg, var(--text-primary, var(--fg, #E8EFE9)));
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--pos-radius-lg, 12px);
    transition: background var(--pos-duration-fast, 120ms) var(--pos-ease-out, ease), border-color var(--pos-duration-fast, 120ms) var(--pos-ease-out, ease), transform var(--pos-duration-fast, 120ms) var(--pos-ease-out, ease);
  }
  .dhk8-lock__key:hover:not(:disabled) { background: var(--pos-surface, var(--bg-surface, #161C18)); }
  .dhk8-lock__key:active:not(:disabled) {
    background: var(--pos-tint, var(--accent, #1F8B3A));
    border-color: var(--pos-tint, var(--accent, #1F8B3A));
    color: #fff;
    transform: scale(0.97);
  }
  .dhk8-lock__key:disabled { opacity: 0.35; cursor: not-allowed; }
  .dhk8-lock__key--empty { background: transparent; border: none; cursor: default; pointer-events: none; }
  .dhk8-lock__key--del { color: var(--pos-fg-tertiary, var(--text-tertiary, var(--fg-muted, #7E8C82))); background: transparent; border-color: transparent; }
  .dhk8-lock__key--del:hover:not(:disabled) { background: var(--pos-tint-soft, color-mix(in oklch, #1F8B3A 12%, transparent)); color: var(--pos-fg, var(--text-primary, #E8EFE9)); }
  .dhk8-lock__key--del:active:not(:disabled) { background: var(--pos-tint-soft, color-mix(in oklch, #1F8B3A 18%, transparent)); border-color: transparent; color: var(--pos-tint, var(--accent, #1F8B3A)); }
`;
//# sourceMappingURL=LockScreen.js.map